import { LayerData, splatLoadingAtom, type SplatLoadingEntry } from '@/atoms';
import { useSetAtom } from 'jotai';
import { lazy, Suspense, useEffect } from 'react';
import { ErrorBoundary } from 'react-error-boundary';

/**
 * Code-split entry point for Gaussian-splat rendering.
 *
 * `@sparkjsdev/spark` is ~2.5 MB of JS, most of it a base64-inlined wasm
 * blob that Spark also starts compiling at module-evaluation time. Importing
 * `./Splat` statically put all of that in the app's entry chunk for every
 * user, splat layers or not. The dynamic import below moves Spark into its
 * own chunk that is fetched the first time a splat layer is shown.
 *
 * Nothing outside this file may import `./Splat` or `@sparkjsdev/spark`
 * as a value (type-only imports are fine) — see
 * `__tests__/splatLazyBoundary.test.ts`.
 */
const Splat = lazy(() => import('./Splat').then((m) => ({ default: m.Splat })));

/**
 * Keeps the splat loading badge honest while the Spark chunk itself is
 * downloading (or failed to download): one `SplatLoadingOverlay` row per
 * layer, indeterminate. Removed again on unmount — only the entries this
 * component wrote, so it never clobbers the progress `SplatMeshNode` takes
 * over with once the chunk has arrived.
 */
const SplatChunkStatus = ({ layers, error }: { layers: LayerData[]; error?: string }) => {
  const setLoading = useSetAtom(splatLoadingAtom);
  const key = layers.map((l) => `${l.id}\u0000${l.name ?? ''}`).join('\u0001');

  useEffect(() => {
    const written = new Map<string, SplatLoadingEntry>();
    for (const layer of layers) {
      if (!layer.root) continue;
      written.set(layer.id, {
        layerId: layer.id,
        layerName: layer.name ?? 'Splat',
        phase: error ? 'failed' : 'fetching',
        error,
      });
    }
    setLoading((prev) => ({ ...prev, ...Object.fromEntries(written) }));
    return () => {
      setLoading((prev) => {
        let next = prev;
        for (const [id, entry] of written) {
          if (next[id] !== entry) continue;
          if (next === prev) next = { ...prev };
          delete next[id];
        }
        return next;
      });
    };
    // `key` captures the identity/name of every layer; `layers` itself is a
    // fresh array on unrelated Viewer re-renders.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, error, setLoading]);

  return null;
};

/**
 * Drop-in for `<Splat layers={…} />` inside the R3F scene. Owns its own
 * Suspense boundary — a suspension that bubbled to the `<Canvas>` root would
 * blank the whole scene while Spark downloads — and its own error boundary,
 * so a failed chunk fetch (e.g. a stale tab after a deploy) shows a failed
 * badge instead of tearing down the viewer.
 */
export const LazySplat = ({ layers }: { layers: LayerData[] }) => (
  <ErrorBoundary
    fallbackRender={({ error }) => (
      <SplatChunkStatus layers={layers} error={(error as Error)?.message ?? 'failed to load splat renderer'} />
    )}
  >
    <Suspense fallback={<SplatChunkStatus layers={layers} />}>
      <Splat layers={layers} />
    </Suspense>
  </ErrorBoundary>
);
